import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { DataManager } from "@syncfusion/ej2-data";
import { AutocompleteCustomAdaptor } from "./autocomplete-custom-adaptor";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import {
  AutoCompleteComponent,
  AutoCompleteModule,
} from "@syncfusion/ej2-angular-dropdowns";
import { IAutocompleteItem } from "./autocomplete-item";
import { IdProvider } from "../id-provider";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-autocomplete",
  templateUrl: "./autocomplete.component.html",
  styleUrls: ["./autocomplete.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AutoCompleteModule, TranslatePipe],
})
export class AutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("autocomplete")
  public autocomplete: AutoCompleteComponent | null = null; // Reference to the Syncfusion AutoComplete component

  @ViewChild("autocomplete", { read: ElementRef, static: false })
  public autocompleteElement: ElementRef | null = null; // Reference to the underlying DOM element

  @Input()
  public id: string = `app-autocomplete-${IdProvider.getNextId()}`;

  @Input({required: true})
  public getDataFunction: ((
      searchText: string | null
    ) => Observable<string[] | IAutocompleteItem[]>) | undefined;

  @Input()
  public placeholder = "i18n.shared.search";

  @Input()
  public autoSelectOnSingleResult: boolean = false;

  @Input()
  public set value(value: any) {
    if (this._outputValue !== value) {
      // set values to 'null' to force reset with data reload
      this._outputValue = value;
      if (this._outputValue) {
        this.autocomplete?.refresh();
      }
    }
  }

  @Input()
  public enabled = true;

  @Input()
  public set isFormComponent(value: boolean) {
    if (this._isFormComponent === value) {
      return;
    }
    this._isFormComponent = value;
    this.updateCssClass();
  }

  @Input()
  public set invalid(value: boolean) {
    if (this._invalid === value) {
      return;
    }
    this._invalid = value;
    this.updateCssClass();
  }

  @Input()
  public minLength: number = 0;

  @Input()
  public allowObjectBinding: boolean = false; // set to true if you use a list of objects

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public touched: EventEmitter<void> = new EventEmitter();

  public fields = { text: "name", value: "id" };
  public get outputValue() {
    return this._outputValue;
  }

  public set outputValue(value: any) {
    if (this._outputValue !== value) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  public data: DataManager | null = null;
  public isFirst = false;
  public cssClass: string | null = null;
  private _invalid = false;
  private _isFormComponent = false;
  private adaptor: AutocompleteCustomAdaptor | null = null;

  private _outputValue: any;

  constructor() {}

  public onSelect(data: any) {
    const value = data.itemData?.id ?? data.itemData ?? null;
    if (this._outputValue != value) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  public onChange(args: any) {
    let value = args.itemData?.id ?? args.itemData ?? null;
    if (isNaN(value)) {
      value = null;
    }
    if (value != this._outputValue) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  ngOnInit(): void {
    this.adaptor = new AutocompleteCustomAdaptor(
      this.getDataFunctionWrapped.bind(this),
    );
    this.data = new DataManager({
      adaptor: this.adaptor,
    });

    if (this.isFormComponent) {
      this.cssClass = "form-autocomplete";
    }
  }

  ngAfterViewInit() {
    //Detect when clear icon gets clicked
    this.autocomplete?.element.addEventListener("input", (e) => {
      if (!this.isFirst) {
        this.isFirst = true;
        this.autocomplete?.element.parentElement
          ?.querySelector(".e-clear-icon")
          ?.addEventListener("mousedown", (args) => {
            this.valueChange.emit();
          });
      }
    });
  }

  public refresh() {
    this.autocomplete?.refresh();
  }

  public clear(): void {
    this._outputValue = null;
    if (this.autocomplete) {
      this.autocomplete.value = null;
      this.autocomplete.text = null;
    }
  }

  public onBlur() {
    this.touched.emit();
    if (!this._outputValue && this.autocomplete?.value) {
      setTimeout(() => {
        if (this.autocomplete) {
          this.autocomplete.value = this._outputValue;
        }
      }, 0);
    }
  }

  public focus(): void {
    // Access the input element of the Syncfusion AutoComplete component
    const inputElement =
      this.autocompleteElement?.nativeElement.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
  }

  ngOnDestroy(): void {
    this.adaptor?.destroy();
  }

  private getDataFunctionWrapped(
    searchText: string | null,
  ): Observable<string[] | IAutocompleteItem[]> {
    if (!this.getDataFunction) {
      return of([]);
    }

    let result$ = this.getDataFunction(searchText);

    if (this.autoSelectOnSingleResult && searchText) {
      result$ = result$.pipe(
        tap((items: any[]) => {
          if (items && items.length === 1) {
            // Fire synchronously before args.onSuccess so state is set before Syncfusion autofills
            this.onSelect({ itemData: items[0] });
          }
        }),
      );
    }

    return result$;
  }

  private updateCssClass() {
    this.cssClass = "";
    if (this._isFormComponent) {
      this.cssClass = "form-autocomplete";
    }
    if (this._invalid) {
      this.cssClass += " e-error";
    }
  }
}
